import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ArrowLeft, Copy, Search, ChevronDown, ChevronRight } from "lucide-react";
import axiosClient from "../../api/axiosClient";

const RECONCILE_ENDPOINT = checkoutSessionId =>
  `/payment/checkout/${checkoutSessionId}/reconcile`;
const RECENT_EVENTS_ENDPOINT = "/analytics/events/recent";

const GUID_REGEX =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/;

const unwrapResponse = res => {
  const body = res?.data ?? {};
  const ok = typeof body.ok === "boolean" ? body.ok : true;
  const data = body?.data ?? body;
  const message = body?.message || data?.message || "";
  return { ok, data, message };
};

const formatDateTime = value => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString("en-IN", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
};

const formatAmount = (amount, currency = "INR") => {
  const n = Number(amount);
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: currency || "INR",
    maximumFractionDigits: 2,
  }).format(Number.isFinite(n) ? n : 0);
};

const decisionBadgeClass = decision => {
  const d = String(decision || "").toLowerCase();
  if (d === "paid") return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (d === "pending") return "bg-amber-50 text-amber-700 border-amber-200";
  if (d === "rejected") return "bg-red-50 text-red-700 border-red-200";
  if (d === "expired") return "bg-slate-100 text-slate-700 border-slate-200";
  return "bg-slate-50 text-slate-700 border-slate-200";
};

const getEventPropsText = eventItem => {
  const raw = eventItem?.propsJson ?? eventItem?.props ?? null;
  if (raw == null) return "";
  if (typeof raw === "string") return raw;
  try {
    return JSON.stringify(raw, null, 2);
  } catch {
    return String(raw);
  }
};

const getEventId = (eventItem, index) =>
  String(eventItem?.id || eventItem?.Id || `${index}`);

export default function PaymentDebugPage() {
  const navigate = useNavigate();

  const [checkoutSessionId, setCheckoutSessionId] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState("");

  const [reconcileData, setReconcileData] = useState(null);
  const [recentEvents, setRecentEvents] = useState([]);
  const [recentEventsError, setRecentEventsError] = useState("");
  const [expandedEventIds, setExpandedEventIds] = useState({});

  const canCopy = !!reconcileData;

  const issues = useMemo(
    () => reconcileData?.inconsistency?.issues || [],
    [reconcileData]
  );

  const mismatch = reconcileData?.mismatch || null;
  const invoice = reconcileData?.invoice || null;
  const transaction = reconcileData?.transaction || null;
  const latestWebhook = reconcileData?.latestWebhook || null;
  const decision = reconcileData?.decision || "NotFound";
  const reasonCode = reconcileData?.reasonCode || "-";

  const onFetch = async () => {
    const id = String(checkoutSessionId || "").trim();
    if (!id) {
      toast.error("Please enter a checkout session id.");
      return;
    }

    if (!GUID_REGEX.test(id)) {
      toast.error("Please enter a valid checkout session id.");
      return;
    }

    setLoading(true);
    setFetchError("");
    setRecentEventsError("");
    setReconcileData(null);
    setRecentEvents([]);
    setExpandedEventIds({});

    const [reconcileResult, eventsResult] = await Promise.allSettled([
      axiosClient.get(RECONCILE_ENDPOINT(id), {
        headers: { "x-suppress-403-toast": true },
      }),
      axiosClient.get(RECENT_EVENTS_ENDPOINT, {
        params: { checkoutSessionId: id, limit: 100 },
        headers: { "x-suppress-403-toast": true },
      }),
    ]);

    if (reconcileResult.status === "fulfilled") {
      const { ok, data, message } = unwrapResponse(reconcileResult.value);
      if (!ok) {
        const msg = message || "Failed to load reconciliation data.";
        setFetchError(msg);
        toast.error(msg);
      } else {
        setReconcileData(data || null);
      }
    } else {
      const msg =
        reconcileResult.reason?.response?.data?.message ||
        "Failed to load reconciliation data.";
      setFetchError(msg);
      toast.error(msg);
    }

    if (eventsResult.status === "fulfilled") {
      const { ok, data, message } = unwrapResponse(eventsResult.value);
      if (!ok) {
        const msg = message || "Failed to load recent analytics events.";
        setRecentEventsError(msg);
        toast.error(msg);
      } else {
        const rows = Array.isArray(data)
          ? data
          : Array.isArray(data?.items)
          ? data.items
          : [];
        setRecentEvents(rows);
      }
    } else {
      const msg =
        eventsResult.reason?.response?.data?.message ||
        "Failed to load recent analytics events.";
      setRecentEventsError(msg);
      toast.error(msg);
    }

    setLoading(false);
  };

  const onCopyJson = async () => {
    if (!reconcileData) return;
    const text = JSON.stringify(reconcileData, null, 2);
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Reconciliation JSON copied.");
    } catch {
      try {
        const textArea = document.createElement("textarea");
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        toast.success("Reconciliation JSON copied.");
      } catch {
        toast.error("Copy failed.");
      }
    }
  };

  const toggleEventExpanded = eventId => {
    setExpandedEventIds(prev => ({ ...prev, [eventId]: !prev[eventId] }));
  };

  return (
    <div className="min-h-screen bg-slate-50 px-4 sm:px-6 py-8">
      <div className="mx-auto w-full max-w-6xl space-y-4">
        <button
          type="button"
          onClick={() => navigate("/app/settings/billing")}
          className="inline-flex items-center gap-2 text-sm text-purple-700 hover:text-purple-800"
        >
          <ArrowLeft size={16} />
          Back to Billing
        </button>

        <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-5 space-y-4">
          <div>
            <h1 className="text-lg font-semibold text-slate-900">Payment Debug</h1>
            <p className="text-xs text-slate-600 mt-1">
              Internal support view for checkout reconciliation and analytics timeline.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_auto_auto]">
            <input
              type="text"
              value={checkoutSessionId}
              onChange={e => setCheckoutSessionId(e.target.value)}
              placeholder="Checkout Session ID"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono"
            />
            <button
              type="button"
              onClick={onFetch}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-purple-600 text-white px-4 py-2 text-sm font-semibold hover:bg-purple-700 disabled:opacity-70"
            >
              <Search size={14} />
              {loading ? "Fetching..." : "Fetch"}
            </button>
            <button
              type="button"
              onClick={onCopyJson}
              disabled={!canCopy}
              className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-300 bg-white text-slate-700 px-4 py-2 text-sm font-semibold hover:bg-slate-50 disabled:opacity-60"
            >
              <Copy size={14} />
              Copy JSON
            </button>
          </div>

          {fetchError && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {fetchError}
            </div>
          )}
        </div>

        {reconcileData && (
          <>
            <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-5 space-y-3">
              <h2 className="text-sm font-semibold text-slate-900">
                Reconciliation Summary
              </h2>
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={`inline-flex rounded-full border px-2 py-1 text-[11px] font-semibold ${decisionBadgeClass(
                    decision
                  )}`}
                >
                  {decision}
                </span>
                <span className="text-xs text-slate-600">
                  ReasonCode: <span className="font-mono text-slate-900">{reasonCode}</span>
                </span>
              </div>

              {issues.length > 0 ? (
                <ul className="list-disc pl-5 text-xs text-red-700 space-y-1">
                  {issues.map(issue => (
                    <li key={issue} className="font-mono">
                      {issue}
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-xs text-slate-500">No inconsistency issues.</div>
              )}
            </div>

            <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-5 space-y-3">
              <h2 className="text-sm font-semibold text-slate-900">Money Snapshot</h2>

              <div className="grid gap-2 text-xs text-slate-700 sm:grid-cols-2">
                <div>
                  Invoice Final:{" "}
                  <span className="font-semibold text-slate-900">
                    {formatAmount(invoice?.finalAmount, invoice?.currency)}
                  </span>
                </div>
                <div>
                  Invoice Discount:{" "}
                  <span className="font-semibold text-slate-900">
                    {formatAmount(invoice?.discountAmount, invoice?.currency)}
                  </span>
                </div>
                <div>
                  Invoice Tax:{" "}
                  <span className="font-semibold text-slate-900">
                    {formatAmount(invoice?.taxAmount, invoice?.currency)}
                  </span>
                </div>
                <div>
                  Currency:{" "}
                  <span className="font-semibold text-slate-900">
                    {invoice?.currency || "-"}
                  </span>
                </div>
              </div>

              {mismatch && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 space-y-1">
                  <div className="font-semibold">Mismatch Diagnostics</div>
                  <div>
                    Expected:{" "}
                    <span className="font-semibold">
                      {formatAmount(mismatch.expectedFinal, mismatch.expectedCurrency)}
                    </span>
                  </div>
                  <div>
                    Received:{" "}
                    <span className="font-semibold">
                      {formatAmount(mismatch.receivedAmount, mismatch.receivedCurrency)}
                    </span>
                  </div>
                  <div>
                    Expected minor:{" "}
                    <span className="font-mono">
                      {mismatch.expectedFinalMinor ?? "-"}
                    </span>
                  </div>
                  <div>
                    Received minor:{" "}
                    <span className="font-mono">
                      {mismatch.receivedAmountMinor ?? "-"}
                    </span>
                  </div>
                  <div>
                    Minor unit:{" "}
                    <span className="font-mono">
                      {mismatch.minorUnitName || "-"}{" "}
                      {mismatch.minorUnitScale != null
                        ? `(scale ${mismatch.minorUnitScale})`
                        : ""}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-5 space-y-3">
              <h2 className="text-sm font-semibold text-slate-900">Transaction</h2>
              {transaction ? (
                <div className="grid gap-2 text-xs text-slate-700 sm:grid-cols-2">
                  <div>
                    Status:{" "}
                    <span className="font-semibold text-slate-900">
                      {transaction.status}
                    </span>
                  </div>
                  <div>
                    Amount:{" "}
                    <span className="font-semibold text-slate-900">
                      {formatAmount(transaction.amount, transaction.currency)}
                    </span>
                  </div>
                  <div>
                    Paid At:{" "}
                    <span className="font-semibold text-slate-900">
                      {formatDateTime(transaction.paidAtUtc)}
                    </span>
                  </div>
                  <div>
                    Txn Id:{" "}
                    <span className="font-mono text-slate-900">
                      {transaction.id || "-"}
                    </span>
                  </div>
                  <div>
                    Gateway Order Id:{" "}
                    <span className="font-mono text-slate-900">
                      {transaction.gatewayOrderId || "-"}
                    </span>
                  </div>
                  <div>
                    Gateway Payment Id:{" "}
                    <span className="font-mono text-slate-900">
                      {transaction.gatewayPaymentId || "-"}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="text-xs text-slate-500">No transaction found.</div>
              )}
            </div>

            <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-5 space-y-3">
              <h2 className="text-sm font-semibold text-slate-900">Latest Webhook</h2>
              {latestWebhook ? (
                <div className="grid gap-2 text-xs text-slate-700 sm:grid-cols-2">
                  <div>
                    Received At:{" "}
                    <span className="font-semibold text-slate-900">
                      {formatDateTime(latestWebhook.receivedAtUtc)}
                    </span>
                  </div>
                  <div>
                    Provider:{" "}
                    <span className="font-semibold text-slate-900">
                      {latestWebhook.provider || "-"}
                    </span>
                  </div>
                  <div>
                    Signature Verified:{" "}
                    <span className="font-semibold text-slate-900">
                      {latestWebhook.signatureVerified ? "Yes" : "No"}
                    </span>
                  </div>
                  <div>
                    Gateway Order Id:{" "}
                    <span className="font-mono text-slate-900">
                      {latestWebhook.gatewayOrderId || "-"}
                    </span>
                  </div>
                  <div className="sm:col-span-2">
                    Gateway Payment Id:{" "}
                    <span className="font-mono text-slate-900">
                      {latestWebhook.gatewayPaymentId || "-"}
                    </span>
                  </div>
                  <div className="sm:col-span-2">
                    Notes:{" "}
                    <span className="font-mono text-slate-800 break-all">
                      {latestWebhook.notes
                        ? String(latestWebhook.notes).slice(0, 700)
                        : "-"}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="text-xs text-slate-500">No webhook metadata found.</div>
              )}
            </div>
          </>
        )}

        <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-5 space-y-3">
          <h2 className="text-sm font-semibold text-slate-900">
            Recent Analytics Events
          </h2>

          {recentEventsError && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {recentEventsError}
            </div>
          )}

          {recentEvents.length === 0 ? (
            <div className="text-xs text-slate-500">No analytics events loaded.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead>
                  <tr className="bg-slate-50 text-slate-600">
                    <th className="text-left px-2 py-2 font-semibold">Time</th>
                    <th className="text-left px-2 py-2 font-semibold">Event</th>
                    <th className="text-left px-2 py-2 font-semibold">
                      ClientRequestId
                    </th>
                    <th className="text-left px-2 py-2 font-semibold">Props</th>
                  </tr>
                </thead>
                <tbody>
                  {recentEvents.map((eventItem, index) => {
                    const eventId = getEventId(eventItem, index);
                    const expanded = !!expandedEventIds[eventId];
                    const propsText = getEventPropsText(eventItem);
                    const shortProps =
                      propsText.length > 180
                        ? `${propsText.slice(0, 180)}...`
                        : propsText;

                    return (
                      <tr key={eventId} className="border-t border-slate-100 align-top">
                        <td className="px-2 py-2 text-slate-700 whitespace-nowrap">
                          {formatDateTime(
                            eventItem?.createdAtUtc ||
                              eventItem?.createdAt ||
                              eventItem?.clientTsUtc
                          )}
                        </td>
                        <td className="px-2 py-2 text-slate-900 font-medium">
                          {eventItem?.eventName || eventItem?.EventName || "-"}
                        </td>
                        <td className="px-2 py-2 text-slate-700 font-mono">
                          {eventItem?.clientRequestId || eventItem?.ClientRequestId || "-"}
                        </td>
                        <td className="px-2 py-2">
                          {propsText ? (
                            <div className="space-y-1">
                              <button
                                type="button"
                                onClick={() => toggleEventExpanded(eventId)}
                                className="inline-flex items-center gap-1 text-purple-700 hover:text-purple-800"
                              >
                                {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                {expanded ? "Collapse" : "Expand"}
                              </button>
                              <pre className="whitespace-pre-wrap break-all rounded border border-slate-200 bg-slate-50 p-2 text-[11px] text-slate-700">
                                {expanded ? propsText : shortProps}
                              </pre>
                            </div>
                          ) : (
                            <span className="text-slate-500">-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
